import type { Cart, CartItem } from "@/types"
import type { Discount } from "@/types/external"
import { catalogItemIdOf, cartLineFor } from "./cart-lines"
import type { Allocation } from "./types"

function discountTarget(allocation: Allocation): Pick<Discount, "target" | "targetId"> | null {
  switch (allocation.target) {
    case "ALLOCATION_TARGET_LINE":
    case "ALLOCATION_TARGET_OPTION":
      return allocation.lineId ? { target: "ITEM", targetId: allocation.lineId } : null
    case "ALLOCATION_TARGET_CART":
      return { target: "CART" }
    case "ALLOCATION_TARGET_DELIVERY_FEE":
      return { target: "DELIVERY_FEE" }
    default:
      return null
  }
}

/**
 * One Open Delivery discount per allocation (contract C6): a line or option
 * share targets the line (`ITEM` + `targetId` = lineId), the rest target the
 * cart or the delivery fee. Each carries exactly one sponsorship whose code is
 * the allocation's own (`<coupon>` or `LOYALTY_RULE_PROMO:<promo>:<rule>`).
 * Cents become BRL here, once. Empty or unknown targets are dropped.
 */
export function allocationsToDiscounts(allocations: readonly Allocation[]): Discount[] {
  return allocations.flatMap((allocation) => {
    const target = discountTarget(allocation)
    if (!target || allocation.amountCents <= 0) return []
    const value = allocation.amountCents / 100
    return [
      {
        amount: { value, currency: "BRL" },
        ...target,
        sponsorshipValues: [
          { name: "MERCHANT", amount: { value, currency: "BRL" }, discountCode: allocation.couponCode },
        ],
      },
    ]
  })
}

/**
 * The line an ITEM discount sits on. New codes target the line position
 * (C6.1); the older free-product and item-promo codes target the catalog item
 * id and sit on the line resolveBenefitDiscount picked: the single-unit line
 * of that item, else its first line.
 */
function itemDiscountLineIndex(cart: Cart, targetId: string | undefined): number {
  if (!targetId) return -1
  if (cartLineFor(cart, targetId)) return Number(targetId)
  const matches = cart.items
    .map((line, index): [CartItem, number] => [line, index])
    .filter(([line]) => catalogItemIdOf(line) === targetId)
  const single = matches.find(([line]) => line.quantity === 1) ?? matches[0]
  return single ? single[1] : -1
}

export type BenefitCents = {
  lines: number[]
  cart: number
  delivery: number
}

/**
 * Where the loyalty benefits already sit, in cents, for coupons/evaluate
 * (C1.1): per line, on the cart, on the delivery fee. An ITEM discount whose
 * line cannot be found counts against the cart, so the net subtotal the
 * coupon is priced on never includes money the customer is not paying.
 */
export function benefitCentsOf(discounts: readonly Discount[], cart: Cart): BenefitCents {
  const out: BenefitCents = { lines: cart.items.map(() => 0), cart: 0, delivery: 0 }
  for (const discount of discounts) {
    const cents = Math.round(discount.amount.value * 100)
    if (cents <= 0) continue
    if (discount.target === "DELIVERY_FEE") {
      out.delivery += cents
      continue
    }
    const index = discount.target === "ITEM" ? itemDiscountLineIndex(cart, discount.targetId) : -1
    if (index >= 0) out.lines[index] += cents
    else out.cart += cents
  }
  return out
}
